/**
 * German texts only the native app shows — camera permissions, the outbox,
 * the hand-over to the web app, the offline band. Everything both clients say
 * is in `de` (@fsv/core) and `tx` (extra.ts).
 *
 * They live here in the shared package, not in apps/mobile, because the help
 * (src/help) describes the app on every platform — the iPhone section is
 * readable on the web too — and quotes these texts through its placeholders
 * instead of repeating them (help.lock.json fails when one of them changes).
 * apps/mobile/src/texts.ts re-exports this object as `mt`.
 */
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const mt = {
  demo: {
    band: 'Demo – Beispieldaten auf diesem Gerät, nichts wird an den Server geschickt.',
    personaTitle: 'Als wer möchtest du dich umsehen?',
    personaHint: 'Fiktive Vereine und Flugzeuge. Kein Konto, kein Netz nötig.',
    personaMember: 'Mitglied – meldet Schäden',
    personaEditor: 'Technik – sichtet und pflegt',
    personaAdmin: 'Administratorin – verwaltet den Verein',
    resetDone: 'Demodaten zurückgesetzt.',
  },
  login: {
    sendCode: 'Code anfordern',
    or: 'oder',
  },
  photos: {
    cameraDenied: 'Ohne Kamerazugriff kann kein Foto aufgenommen werden. Bitte in den Einstellungen erlauben.',
    libraryDenied: 'Ohne Zugriff auf die Mediathek kann kein Foto gewählt werden. Bitte in den Einstellungen erlauben.',
    stillHasMetadata: 'Dieses Foto enthält nach dem Verkleinern noch Metadaten und wird nicht verwendet.',
  },
  outbox: {
    pending: 'ausstehend',
    refused: 'nicht angenommen',
    photosPending: (n: number) => `${n} ${plural(n, 'Foto', 'Fotos')} ausstehend`,
    pendingLabel: (n: number) => `${n} ${plural(n, 'Meldung wartet', 'Meldungen warten')} auf Verbindung`,
    sending: 'Wird gesendet …',
    queued: 'Gespeichert – wird gesendet, sobald eine Verbindung besteht.',
    sent: (ref: string) => `${ref} gesendet.`,
    failed: (title: string, message: string) => `„${title}“ wurde nicht angenommen: ${message}`,
    discard: 'Verwerfen',
    retryNow: 'Jetzt senden',
    nextTry: (time: string) => `Nächster Versuch ${time}`,
    photosRefused: (n: number) => `${n} ${plural(n, 'Foto wurde', 'Fotos wurden')} nicht angenommen.`,
    title: 'Warteschlange',
    /** Local notification after the background task sent a report (outbox/background.ts). */
    sentInBackgroundTitle: 'Meldung gesendet',
    sentInBackground: (ref: string, registration: string) => `${ref} (${registration}) ist angekommen.`,
    failedInBackgroundTitle: 'Meldung nicht angenommen',
    backgroundHint: 'Auch bei geschlossener App versucht das Gerät, wartende Meldungen zu senden, sobald es wieder Netz hat. Wann, entscheidet das System – auf dem iPhone oft erst nach Stunden, etwa nachts beim Laden. Sofort geht es, sobald du die App öffnest.',
    backgroundLast: (time: string, detail: string) => `Letzter Versuch im Hintergrund: ${time} – ${detail}.`,
    backgroundNone: 'Im Hintergrund noch kein Versuch: Das System hat die App dafür bisher nicht geweckt.',
    backgroundSent: (n: number) => `${n} ${plural(n, 'Meldung', 'Meldungen')} gesendet`,
    backgroundNothing: 'nichts gesendet',
    backgroundNoSession: 'Anmeldung nicht lesbar (Gerät seit dem Neustart nicht entsperrt)',
    backgroundError: 'fehlgeschlagen',
  },
  web: {
    title: 'Verwaltung',
    hint: 'Mitglieder, Flotte, Protokoll, Papierkorb und Einstellungen gibt es in der Web-App.',
    open: 'In der Web-App öffnen',
  },
  plan: {
    pinchHint: 'Mit einem Finger verschieben, mit zwei Fingern zoomen.',
    placeHint: 'Auf die Stelle tippen – oder Finger gedrückt halten: die Lupe zeigt, wo das Kreuz landet.',
    damages: (n: number) => `${n} ${plural(n, 'Schaden', 'Schäden')} in dieser Ansicht`,
  },
  // The notices of the technicians written in the app (owner, 18.10.2026): online only, like „Position ändern“.
  notices: {
    offline: 'Hinweise anlegen, ändern und aufheben geht in der App nur mit Verbindung.',
    dayPlaceholder: 'TT.MM.JJJJ',
    dayInvalid: 'Bitte einen Tag als TT.MM.JJJJ angeben, z. B. 31.10.2026.',
    dayPast: 'Dieser Tag liegt in der Vergangenheit.',
    bodyTooLong: (max: number) => `Höchstens ${max} Zeichen.`,
    readersShow: 'Wer hat gelesen?',
    readersHide: 'Liste ausblenden',
  },
  offline: {
    banner: 'Offline – gespeicherter Stand',
  },
  tenant: {
    title: 'Deine Vereine',
    intro: 'Du bist Mitglied in folgenden Vereinen:',
    none: 'Für dieses Konto ist kein Verein freigeschaltet.',
  },
  legal: {
    imprint: 'Impressum',
    privacy: 'Datenschutz',
  },
  push: {
    explainTitle: 'Benachrichtigungen',
    explainBody:
      'Außencheck kann dir eine Nachricht schicken, wenn sich an einem Schaden, den du gemeldet hast, etwas tut – und, wenn du in der Technik bist, sobald jemand einen neuen Schaden meldet.',
    explainPrivacy:
      'Die Nachricht nennt das Kennzeichen und den Titel des Schadens oder den neuen Status, keine Namen. Abschalten kannst du sie jederzeit im Profil oder in den Einstellungen des Geräts.',
    allow: 'Erlauben',
    later: 'Später',
    off: 'Benachrichtigungen sind für diese App ausgeschaltet.',
    turnOn: 'Benachrichtigungen erlauben',
    demo: 'In der Demo gibt es keine Benachrichtigungen.',
    loadFailed: 'Die Einstellungen konnten nicht geladen werden.',
    loading: 'Einstellungen werden geladen …',
    saveFailed: 'Die Änderung wurde nicht gespeichert.',
  },
} as const;
